import React from 'react';
import { toast, ToastOptions, TypeOptions } from 'react-toastify';
import { BtnLink } from '../components/btn-link/Btn';
import { BRAND_ASSETS_BASE } from '../constants';

function getToastMethod(type: TypeOptions): 'success' | 'info' | 'warn' | 'error' | 'default' {
  switch (type) {
    case 'success':
      return 'success';
    case 'info':
      return 'info';
    case 'warning':
      return 'warn';
    case 'error':
      return 'error';
    case 'default':
      return 'default';
  }

  console.error('Invalid notification method: ' + type);
  return 'default';
}

interface NotifyConfig {
  readonly type: TypeOptions;
  readonly message: React.ReactNode;
  readonly icon?: React.ReactNode | false;
  readonly link?: {
    readonly text: string;
    readonly to: string;
  };
  readonly options?: ToastOptions;
}

type TypeConfig = React.ReactNode | Omit<NotifyConfig, 'type'>;

function messageIsNode(m: React.ReactNode | TypeConfig): m is React.ReactNode {
  return typeof (m as any).message === 'undefined';
}

function parseConfig(config: TypeConfig): Omit<NotifyConfig, 'type'> {
  if (messageIsNode(config)) {
    return {
      message: config,
    };
  }

  return config;
}

export const notifySuccess = (config: TypeConfig) => {
  notify({ type: 'success', ...parseConfig(config) });
};

export const notifyInfo = (config: TypeConfig) => {
  notify({ type: 'info', ...parseConfig(config) });
};

export const notifyWarn = (config: TypeConfig) => {
  notify({ type: 'warning', ...parseConfig(config) });
};

export const notifyError = (config: TypeConfig) => {
  notify({ type: 'error', ...parseConfig(config) });
};

const showNotification = (title: string, options?: NotificationOptions): void => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, options);
  }
};

export const notify = (config: NotifyConfig) => {
  const { icon, type, message: passedMessage, link, options } = config;

  if (typeof passedMessage === 'string') {
    const title = passedMessage.charAt(0).toUpperCase() + passedMessage.slice(1);
    Notification.requestPermission().then((permission) => {
      showNotification(title, {
        body: passedMessage,
        icon: `${BRAND_ASSETS_BASE}/favicon.ico`,
      });
    });
  }

  let message = <React.Fragment>{passedMessage}</React.Fragment>;

  if (link) {
    message = (
      <div>
        {message}
        <div
          style={{ marginTop: '0.5rem' }}
          onClick={() => {
            // Close the notification when link area is clicked
            toast.dismiss();
          }}
        >
          <BtnLink type="outline-primary" size="xs" className="mt-1" to={link.to}>
            {link.text}
          </BtnLink>
        </div>
      </div>
    );
  }

  // @ts-ignore
  const notifyMethod = type === 'default' ? toast : toast[getToastMethod(type)];

  try {
    notifyMethod(message, {
      pauseOnFocusLoss: false,
      ...(options || {}),
    });
  } catch (error) {
    console.error('Error showing notification:', error);
  }
};

notify.success = notifySuccess;
notify.info = notifyInfo;
notify.warn = notifyWarn;
notify.error = notifyError;
